import React, { useState } from "react";
import { Form, Formik } from "formik";
import orderBy from "lodash/orderBy";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router";

import useAuth from "../../../hooks/useAuth";
import useMutation from "../../../hooks/useMutation";
import { useShopping } from "../../../contexts/shopping";
import useNotification from "../../../hooks/notification";
import { isTimeLessThan48Hours } from "../../../utils/inventoryRequests";
import { MAKE_SHOPPING_INVENTORY_REQUEST_MUTATION, MAKE_SHOPPING_INVENTORY_REQUEST_ASYNC_MUTATION } from "../../../constants/graphql/mutations";
import { ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION } from "../../../constants/graphql/mutations";
import useAdmin from "../../../hooks/useAdmin";

const singleAddressCondition = () => {
  return Yup.string().when("deliveryType", {
    is: deliveryType => deliveryType === "multiple",
    then: schema => schema,
    otherwise: schema => schema,
  });
};

const requiredAddressSchema = Yup.object().shape({
  street: Yup.string(),
  city: Yup.string(),
  zipCode: Yup.string(),
  comment: Yup.string(),
  assetId: Yup.string(),
});

export const validateMultipleEmails = value => {
  if (!value || value.trim() === "") return true;

  const emails = value
    .split(/[,;]/)
    .map(email => email.trim())
    .filter(email => email.length > 0);

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emails.every(email => emailRegex.test(email));
};

export const validationSchema = ({ email, requireRecipientEmails = false }) =>
  Yup.object().shape({
    street: singleAddressCondition(),
    city: singleAddressCondition(),
    zipCode: singleAddressCondition(),
    comment: Yup.string(),
    ccEmails: (requireRecipientEmails
      ? Yup.string().trim().required("Required")
      : Yup.string().nullable()
    ).test(
      "multiple-emails",
      "Invalid email format. Use comma or semicolon to separate multiple emails.",
      validateMultipleEmails
    ),
    addresses: Yup.mixed().when("deliveryType", {
      is: deliveryType => deliveryType === "multiple",
      then: () => Yup.array().of(requiredAddressSchema),
      otherwise: () => Yup.array(),
    }),
  });

const CartForm = ({ setErrors, initialValues, children, setSubmitting, email }) => {
  const { projectId } = useParams();
  const { notifySuccess } = useNotification();
  const [makeShoppingInventoryRequest] = useMutation(MAKE_SHOPPING_INVENTORY_REQUEST_MUTATION);
  const [makeShoppingInventoryRequestAsync] = useMutation(MAKE_SHOPPING_INVENTORY_REQUEST_ASYNC_MUTATION);
  const { inventoryItems, inventoryRequest } = useShopping();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { notifyError } = useNotification();
  const isAdmin = useAdmin();
  const [mutationInProgress, setMutationInProgress] = useState(false);

  const [inventoryRequestAddAttachment] = useMutation(ADD_INVENTORY_REQUEST_ATTACHMENT_MUTATION);

  const onSubmit = (values, { setErrors: setFormErrors, setStatus }) => {
    if (!isAdmin && isTimeLessThan48Hours(values)) {
      return notifyError("Please select a date and time at least 48 hours in advance");
    }

    // Prevent duplicate mutations (but allow re-validation)
    if (mutationInProgress) {
      return;
    }

    const { file, locationNotes, locationOrder = {}, ...input } = values;
    input.hasCheckoutAttachments = Boolean(file);
    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.get("reserve")) {
      input.vaStatus = {
        canceled: false,
        completed: false,
        pending_approval: false,
        picked: false,
        scheduled: false,
        submitted: false,
        change_order: false,
        reserved: true,
      };
    } else {
      input.vaStatus = {
        canceled: false,
        completed: false,
        pending_approval: false,
        picked: false,
        scheduled: false,
        submitted: true,
        change_order: false,
        reserved: false,
      };
    }

    if (locationNotes) {
      input.locationNotes = orderBy(
        Object.entries(locationNotes).map(([location, note], index) => ({
          location,
          note,
          position: locationOrder[location],
        })),
        "position"
      );
    }

    setSubmitting(true);
    setMutationInProgress(true);

    const itemsCount = Object.keys(inventoryItems || {}).length;
    const companyThresholdRaw = user?.company?.settings?.async_order_processing_items_threshold;
    const companyThreshold = Number.parseInt(companyThresholdRaw, 10);
    const threshold = Number.isNaN(companyThreshold) ? 50 : companyThreshold;
    const useAsync = itemsCount > threshold;

    const mutation = useAsync ? makeShoppingInventoryRequestAsync : makeShoppingInventoryRequest;

    mutation({
      variables: { input, projectId, inventoryRequestId: inventoryRequest?.id },
      onSuccess: ({ inventoryRequest } = {}) => {
        setSubmitting(false);
        setMutationInProgress(false);
        setStatus({ success: true });
        if (useAsync) {
          notifySuccess("Inventory Request processing. We’ll notify you when it’s ready.");
          navigate(`/shopping/cart/submitted`);
          return;
        }

        notifySuccess("Inventory Request has been successfully created!");

        if (file) {
          inventoryRequestAddAttachment({
            variables: { inventoryRequestId: inventoryRequest.id, file },
          });
        }

        const isExtendedCheckout = user.company.settings.wizard_mode || user.company.settings.simple_checkout_for_contract_warehouse;
        if (isExtendedCheckout) {
          const workOrderNumber = encodeURIComponent(inventoryRequest.workOrderNumber);
          const confirmationUrl = `/shopping/cart/confirmation?id=${inventoryRequest.id}&workOrderNumber=${workOrderNumber}`;
          navigate(confirmationUrl);
        } else {
          projectId ? navigate(`/shopping/${projectId}`) : navigate(`/shopping`);
        }
      },
      onFailure: errors => {
        setStatus({ success: false });
        setFormErrors(errors);
        if (setErrors) setErrors(errors);
        setSubmitting(false);
        setMutationInProgress(false);
      },
    });
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={initialValues}
      validationSchema={validationSchema({ email })}
      onSubmit={onSubmit}
    >
      <Form>{children}</Form>
    </Formik>
  );
};

export default props => {
  return <CartForm {...props} />;
};
